'use client'

import React, { useContext, createContext, PropsWithChildren, useCallback } from 'react'
import { useApi } from '@/contexts/ApiContext'
import { InsurancePolicy } from '@/types/interfaces/insurance-policy'
import useSessionStorage from '@/hooks/useSessionStorage'

interface InsuranceContextType {
    userInsurance: InsurancePolicy | null
    setUserInsurance: (insurance: InsurancePolicy | null) => void
    fetchUserInsurancePolicy: () => Promise<void>
    refreshInsurance: () => Promise<void>
}

const InsuranceContext = createContext<InsuranceContextType>({
    userInsurance: null,
    setUserInsurance: () => undefined,
    fetchUserInsurancePolicy: async () => undefined,
    refreshInsurance: async () => undefined,
})

export const InsuranceProvider = ({ children }: PropsWithChildren) => {
    const { apiGet, apiPost } = useApi()
    const [userInsurance, setUserInsurance] = useSessionStorage<InsurancePolicy | null>('userInsurance', null)

    const fetchUserInsurancePolicy = useCallback(async () => {
        try {
            const response = await apiGet('/external/insurances/show')
            setUserInsurance(response)
        } catch (error) {
            setUserInsurance(null)
            console.error('Error fetching user insurance policy', error)
        }
    }, [apiGet, setUserInsurance])

    const refreshInsurance = async () => {
        await apiPost('/external/insurances/refresh/')
    }

    return (
        <InsuranceContext.Provider
            value={{
                userInsurance,
                setUserInsurance,
                fetchUserInsurancePolicy,
                refreshInsurance,
            }}
        >
            {children}
        </InsuranceContext.Provider>
    )
}

export const useInsuranceContext = () => useContext(InsuranceContext)
